import type { Clip, EndedLiveStream } from '@/lib/stream'
import type { TxStatus } from '@/lib/ledger'

const EXPLORER = process.env.NEXT_PUBLIC_BLOCKCHAIN_EXPLORER_URL || ''

export interface LedgerTxEntry {
  label: string
  status: TxStatus
  txHash?: string
  explorerUrl?: string
}

export interface LedgerStreamEntry {
  id: string
  title: string
  username: string | null
  avatarUrl: string | null
  category: string | null
  originalUrl: string | null
  streamedAt: string
  endedAt: string
  duration: number | null
  broadcastId: string
  liveStreamId: string
  chainStreamId: string | null
  initialSignature: string | null
  finalSignature: string | null
  chainContractAddress: string | null
  chainExplorerUrl: string
  ledgerVisibility: string[] | null
  txStreamStart: LedgerTxEntry
  txStreamEnd: LedgerTxEntry
  txMp4Hash: LedgerTxEntry
  /** On-chain clip TX rows — one per clip that has a confirmed clipTxHash */
  clipTxs: LedgerTxEntry[]
  chainRecorded: boolean
}

function txEntry(
  label: string,
  hash: string | null | undefined,
  explorer: string
): LedgerTxEntry {
  if (hash) {
    return {
      label,
      status: 'completed',
      txHash: hash,
      explorerUrl: explorer ? `${explorer}/tx/${hash}` : undefined,
    }
  }
  return { label, status: 'not-started' }
}

function clipTxEntries(clips: Clip[] | undefined, fallbackExplorer: string): LedgerTxEntry[] {
  if (!clips?.length) return []
  return clips.flatMap((c, i): LedgerTxEntry[] => {
    const cd = c.chainData
    if (!cd?.clipTxHash) return []
    const explorer = cd.chainExplorerUrl || fallbackExplorer
    return [
      {
        label: c.title?.trim() || `Clip ${i + 1}`,
        status: 'completed',
        txHash: cd.clipTxHash,
        explorerUrl: explorer ? `${explorer}/tx/${cd.clipTxHash}` : undefined,
      },
    ]
  })
}

export function mapToLedgerEntry(ls: EndedLiveStream): LedgerStreamEntry {
  const cd = ls.chainData
  const explorer = cd?.chainExplorerUrl || EXPLORER

  return {
    id: ls.id,
    title: ls.title || ls.metadata?.title || 'Untitled Stream',
    username: ls.user?.username ?? null,
    avatarUrl: ls.user?.avatarUrl || ls.user?.picture || null,
    category: ls.category ?? null,
    originalUrl: ls.originalUrl ?? null,
    streamedAt: ls.createdAt,
    endedAt: ls.endedAt,
    duration: ls.duration,
    broadcastId: ls.broadcastId,
    liveStreamId: ls.id,
    chainStreamId: cd?.chainStreamId ?? null,
    initialSignature: ls.initialSignature,
    finalSignature: ls.finalSignature,
    chainContractAddress: cd?.chainContractAddress ?? null,
    chainExplorerUrl: explorer,
    ledgerVisibility: ls.ledgerVisibility ?? null,
    txStreamStart: txEntry('Stream Start', cd?.startTxHash, explorer),
    txStreamEnd: txEntry('Stream End', cd?.stopTxHash, explorer),
    txMp4Hash: txEntry('MP4 Validation Hash', cd?.mp4TxHash, explorer),
    clipTxs: clipTxEntries(ls.clips, explorer),
    chainRecorded: cd?.chainRecorded ?? false,
  }
}
